import { useEffect, useState } from "react";
import api from "../../../utils/axiosInstance";
import AllContactsTable from "./AllContactsTable";
import AddContactPersonForm from "../../admin/Vendors/VendorDetails/ContactPersons/AddContactPersonForm";
import EditContactPersonForm from "../../admin/Vendors/VendorDetails/ContactPersons/EditContactPersonForm";
import { toast, confirmDialog } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";
import FormSelect from "../../../components/Form/FormSelect";

const vendorLabel = (v) => {
  const primaryName = `${v.primary_users?.first_name || ""} ${
    v.primary_users?.last_name || ""
  }`.trim();
  const code = `VDR-${String(v.id).padStart(3, "0")}`;
  const name =
    v.company_name || v.legal_entity || primaryName || v.auth?.email || `Vendor #${v.id}`;
  return `${code} — ${name}`;
};

const VendorContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // view: "list" | "add" | "edit"
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const [addVendorId, setAddVendorId] = useState("");

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await api.get("vendor/contact-persons", {
        withCredentials: true,
      });
      setContacts(res.data.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch contact persons");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await api.get("/vendors", { withCredentials: true });
      setVendors(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchVendors();
  }, []);

  const handleDelete = async (id) => {
    if (!await confirmDialog("Are you sure you want to delete this contact person?"))
      return;
    try {
      await api.delete(`vendor/contact-persons/${id}`, {
        withCredentials: true,
      });
      fetchContacts();
    } catch (err) {
      toast.error("Failed to delete contact person");
    }
  };

  const handleAdd = async (data) => {
    if (!addVendorId) {
      throw new Error("Please select a vendor for this contact person");
    }
    await api.post("vendor/contact-persons", {
      ...data,
      vendor_id: Number(addVendorId),
    });
    setView("list");
    setAddVendorId("");
    fetchContacts();
  };

  const handleUpdate = async (id, data) => {
    await api.put(`vendor/contact-persons/${id}`, data, {
      withCredentials: true,
    });
    setView("list");
    setEditing(null);
    fetchContacts();
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  if (error)
    return <div className="text-center mt-10 text-danger-600">{error}</div>;

  if (view === "add") {
    return (
      <div className="space-y-4">
        <div className="max-w-md">
          <label
            htmlFor="add-vendor-select"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Vendor <span className="text-danger-500">*</span>
          </label>
          <FormSelect
            id="add-vendor-select"
            value={addVendorId}
            onChange={(e) => setAddVendorId(e.target.value)}
            options={vendors.map((v) => ({ value: v.id, label: vendorLabel(v) }))}
            placeholder="Select a vendor"
          />
        </div>

        <AddContactPersonForm
          onCancel={() => {
            setView("list");
            setAddVendorId("");
          }}
          onSave={handleAdd}
        />
      </div>
    );
  }

  if (view === "edit" && editing) {
    return (
      <EditContactPersonForm
        person={editing}
        onCancel={() => {
          setView("list");
          setEditing(null);
        }}
        onSave={handleUpdate}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Contacts</h1>
        <button
          onClick={() => setView("add")}
          className={btn("primary", "sm")}
        >
          Add Contact Person
        </button>
      </div>

      <p className="text-sm text-gray-600">
        Showing <span className="font-semibold">{contacts.length}</span> items.
      </p>

      <AllContactsTable
        contacts={contacts}
        onEdit={(person) => {
          setEditing(person);
          setView("edit");
        }}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default VendorContactsPage;
